import { gql } from '@apollo/client';

export const BOOKING = gql`
  query Booking($bookingCode: String!) {
    booking(bookingCode: $bookingCode) {
      id
      created
      updated
      code
      paymentRequestReference {
        paymentServicePaymentRequestId
      }
      bookerInfo {
        firstName
        lastName
        email
        phone
        companyName
        reference
        traveller
        receiveJourneyUpdates
        languageIsoCode
      }
      partner {
        name
        communicationPreferences {
          sendJourneyCancellationEmails
        }
        styleConfiguration {
          backgroundColor
          backgroundContrastColor
          primaryColor
          primaryContrastColor
          secondaryColor
          secondaryContrastColor
          tertiaryColor
          tertiaryContrastColor
          heroImageUrl
          logoUrl
        }
      }
    }
    journeysByBookingCode(bookingCode: $bookingCode) {
      results {
        id
        travellerInfo {
          flightNumber
          passengerCount
          luggageCount
          firstName
          lastName
          email
          phone
          meetAndGreetSignText
          driverComments
          trainNumber
          languageIsoCode
          residenceCountryCode
          displayEmailAllowed
        }
        journeyExecutionInfo {
          vehicleCategory
        }
        journeyCode
      }
    }
  }
`;

export const BOOKING_DETAILS = gql`
  query BookingDetails($bookingCode: String!, $lastName: String!, $skip: Boolean!) {
    booking(bookingCode: $bookingCode, lastName: $lastName) @skip(if: $skip) {
      id
      created
      updated
      code
      paymentRequestReference {
        paymentServicePaymentRequestId
      }
      bookerInfo {
        firstName
        lastName
        email
        phone
        companyName
        reference
        traveller
        receiveJourneyUpdates
        languageIsoCode
      }
    }
  }
`;

export const JOURNEYS_BY_BOOKING_CODE = gql`
  query JourneysByBookingCode($bookingCode: String!, $includePickupTimeline: Boolean!) {
    journeysByBookingCode(bookingCode: $bookingCode) {
      results {
        id
        bookingId
        journeyCode
        originLocationId
        destinationLocationId
        inbound
        status
        originLocation {
          address {
            formattedAddress
            originalAddress
          }
          hub
          hubData{
            autoCompleteName
            hubType
          }
        }
        destinationLocation {
          address {
            formattedAddress
            originalAddress
          }
          hub
          hubData{
            autoCompleteName
            hubType
          }
        }
        hub {
          id
          autoCompleteName
          details {
            code
            value
            name
          }
        }
        journeyExecutionInfo {
          pickupDate
          pickupTimeZone
          pickupTimeAdjustmentMethod
        }
        travellerInfo {
          flightNumber
          passengerCount
          luggageCount
          firstName
          lastName
          email
          phone
          meetAndGreetSignText
          driverComments
          trainNumber
          languageIsoCode
          residenceCountryCode
          displayEmailAllowed
          allowedToModifyJourney
          shipName
        }
        pickupTimeline @include(if: $includePickupTimeline) {
          flightArrivalTime
          pickupTime
          suggestedPickupTime
          freeWaitingTimeInMinutes
          bufferTimeInMinutes
          accommodateFlightDelaysInHours
          steps {
            type
            timestamp
          }
          notifications {
            severity
            type
          }
        }
      }
    }
  }
`;

export const JOURNEY_CHANGES_BY_JOURNEY_ID = gql`
  query JourneyChangesByJourneyId($id: Int!, $skip: Boolean!) {
    journeyChangesByJourneyId(id: $id) @skip(if: $skip) {
      valueChanges {
        oldValue
        newValue
        fieldName
      }
    }
  }
`;

export const JOURNEY = gql`
  query Journey($id: Int!, $bookingCode: String!, $includeCancellationDetails: Boolean, $language: String, $includePickupTimeline: Boolean!) {
    journey(params: { id: $id, bookingCode: $bookingCode, includeCancellationDetails: $includeCancellationDetails, language: $language }) {
      id
      hubId
      journeyCode
      bookingId
      totalPrice
      canBeCompleted
      journeyChangesInfo {
        afterMinBookingWindow
      }
      statusChangeHistory {
        previousStatus
        newStatus
        created
      }
      hub {
        id
        autoCompleteName
        minBookWindowHours
        hubType
        details {
          code
          value
          name
        }
      }
      originLocation {
        address {
          formattedAddress
          originalAddress
        }
        position {
          lat
          lng
        }
        hub
        hubData {
          hubType
          autoCompleteName
        }
      }
      cancellationDetails {
        freeCancellationUntil
        cancellable
      }
      destinationLocation {
        address {
          formattedAddress
          originalAddress
        }
        position {
          lat
          lng
        }
        hub
        hubData {
          hubType
          autoCompleteName
        }
      }
      travellerInfo {
        firstName
        lastName
        passengerCount
        luggageCount
        flightNumber
        trainNumber
        shipName
        meetAndGreetSignText
        driverComments
        phone
        email
        languageIsoCode
        residenceCountryCode
        allowedToModifyJourney
        displayEmailAllowed
      }
      journeyExecutionInfo {
        pickupTimeAdjustmentMethod
        vehicleCategory
        pickupDate
        pickupTimeZone
        cancellationReason
        fleetId
        driverId
        vehicleId
        meetingPointId
        meetingPointType
        transferCompanyId
        assignedDriverId
        assignedDriverVehicleId
        lastKnownDriverPositionUpdated
        lastKnownDriverPosition {
          lat
          lng
        }
      }
      priceCalculationInfo {
        currencyCode
      }
      status
      inbound
      transferCompany {
        id
        phone
        organizationId
        organization {
          name
        }
      }
      travelAddons {
        type
        amount
      }
      fleet {
        id
        name
        customerSupportInfo {
          phone
          email
        }
      }
      vehicle {
        id
        licensePlate
        vehicleMake
        vehicleModel
        category
      }
      driver {
        id
        name
        mobilePhoneNumber
        photoFileExternalKey
      }
      assignedMeetingPoint(language: $language) {
        id
        title
        geographicLocation {
          lat
          lng
        }
        description
        files {
         file {
          externalKey
          contentType
         }
         fileType
         fileId
        }
      }
      quote {
        passengerCapacity
        luggageCapacity
      }
      pickupTimeline @include(if: $includePickupTimeline) {
        flightArrivalTime
        pickupTime
        suggestedPickupTime
        freeWaitingTimeInMinutes
        bufferTimeInMinutes
        accommodateFlightDelaysInHours
        steps {
          type
          timestamp
        }
        notifications {
          severity
          type
        }
      }
    }
    booking(bookingCode: $bookingCode) {
      id
      pinCode
      prepaid
      creationInfo {
        source
      }
      partner {
        name
        communicationPreferences {
          sendJourneyCancellationEmails
        }
        applicationSettings{
          results {
            code
            value
          }
        }
      }
      eligibleForGratuity(journeyId: $id)
      gratuityRecomendations(journeyId: $id) {
          amount
          percentageReference
          currencyCode
        }
      bookerInfo {
        firstName
        lastName
        email
        phone
        companyName
        reference
        traveller
        receiveJourneyUpdates
        languageIsoCode
      }
    }
  }
`;

export const DRIVER_POSITION = gql`
  query DriverPosition($id: Int!) {
    driverPosition(id: $id) {
      position {
        lat
        lng
      }
      recordedAt
      estimatedTimeArrivalAtPickup
      estimatedTimeArrivalAtDropOff
    }
  }
`;

export const CANCEL_JOURNEY = gql`
  mutation CancelJourney($params: JourneyCancellationParams!) {
    cancelJourney(params: $params)
  }
`;

export const PROCESS_JOURNEY_CHANGES = gql`
  mutation ProcessJourneyChanges($params: JourneyParams!) {
    processJourneyChanges(params: $params) {
      id
      isPaymentRequired
      changeAutomationType
      paymentAmount
    }
  }
`;

export const CONFIRM_JOURNEY_CHANGES = gql`
  mutation ConfirmJourneyChanges($id: Int!, $overridePayment: Boolean) {
    confirmJourneyChanges(id: $id, overridePayment: $overridePayment)
  }
`;

export const INITIATE_PAYMENT = gql`
  mutation InitiatePayment($id: Int!) {
    initiatePayment(id: $id) {
      paymentServicePaymentRequestId
      paymentRequest {
        amount
        currencyCode
      }
    }
  }
`;

export const UNRESOLVED_ALERTS = gql`
  query UnresolvedAlerts($params: UnresolvedAlertsRequestParams!, $skip: Boolean!) {
    unresolvedAlerts(params: $params) @skip(if: $skip) {
      id
      created
      updated
      type
      journeyIds
    }
  }
`;

export const INITIATE_PHONE_VERIFICATION = gql`
  mutation InitiatePhoneVerification($id: Int!, $phoneNumber: String!) {
    initiatePhoneVerification(id: $id, phoneNumber: $phoneNumber)
  }
`;

export const RESOLVE_ALERT = gql`
  mutation ResolveAlert($token: String!) {
    resolveAlert(token: $token)
  }
`;

export const RESOLVE_ALERT_WITH_CHANGE_ID = gql`
  mutation ResolveAlertWithChangeId($alertId: Int!, $journeyChangeIds: [Int]!) {
    resolveAlertWithChangeId(alertId: $alertId, journeyChangeIds: $journeyChangeIds)
  }
`;

export const VERIFY_ELIGIBILITY = gql`
  query VerifyEligibility( $journeyId: Int!, $bookingPinCode: String){
    verifyGratuityEligibilityForJourney(params:{journeyId: $journeyId, bookingPinCode: $bookingPinCode}){
      eligible
      recommendations(params:{journeyId: $journeyId, bookingPinCode: $bookingPinCode, currencyCode: "EUR"}) {
        amount
        percentageReference
        currencyCode
      }
    }
}`;

export const CREATE_GRATUITY = gql`
  mutation CreateGratuityForJourney($params: CreateGratuityParams!, $optionalPaymentLinkParams: OptionalPaymentLinkParams) {
    createGratuityForJourney(params: $params, optionalPaymentLinkParams: $optionalPaymentLinkParams) {
      id
      paymentRequestId
      paymentLink {
        url
        expiresAt
      }
    }
  }
`;

export const UPDATE_TRAVELLER_POSITION = gql`
  mutation UpdateTravellerPosition($params: TravellerPositionsParams) {
    updateTravellerPosition(params: $params)
  }
`;

